import { useState, useEffect, useCallback, useRef } from 'react';
import { FilterStorage, DEFAULT_FILTER_TTL_MINUTES } from '~/utils/filter-storage';

var isSameFilters = function(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
};

/**
 * usePersistedFilters — page filter state that survives a refresh for a few
 * minutes (see src/utils/filter-storage.js).
 *
 *   var persisted = usePersistedFilters('documents', DEFAULT_FILTERS);
 *   persisted.filters          // current filters (defaults merged with saved)
 *   persisted.setFilters({ status: 'overdue' })   // merge a partial update
 *   persisted.resetFilters()   // back to defaults, clears storage
 *   persisted.isDefault        // true when nothing differs from defaults
 *   persisted.wasRestored      // true when the page opened with saved filters
 *   persisted.dismissRestored()
 *
 * `defaults` must be JSON-serializable (dates as strings). Pass a function
 * when defaults depend on "now" (e.g. current month) so they are evaluated
 * at mount.
 */
var usePersistedFilters = function(pageKey, defaults, options) {
  var opts = options || {};
  var ttlMinutes = opts.ttlMinutes || DEFAULT_FILTER_TTL_MINUTES;

  var defaultsRef = useRef(null);
  if (defaultsRef.current === null) {
    defaultsRef.current = typeof defaults === 'function' ? defaults() : defaults;
  }

  var initialRef = useRef(null);
  if (initialRef.current === null) {
    var saved = FilterStorage.read(pageKey, { ttlMinutes: ttlMinutes });
    var merged = Object.assign({}, defaultsRef.current, saved || {});
    initialRef.current = {
      filters: merged,
      wasRestored: !!saved && !isSameFilters(merged, defaultsRef.current),
    };
  }

  var [filters, setFiltersState] = useState(initialRef.current.filters);
  var [wasRestored, setWasRestored] = useState(initialRef.current.wasRestored);

  var isDefault = isSameFilters(filters, defaultsRef.current);

  useEffect(function() {
    if (isDefault) {
      FilterStorage.remove(pageKey);
      return undefined;
    }
    FilterStorage.save(pageKey, filters);

    // Re-stamp on refresh / leaving the page, so the TTL counts from when the
    // user left, not from when the filter was last changed.
    var touch = function() { FilterStorage.save(pageKey, filters); };
    window.addEventListener('beforeunload', touch);
    return function() {
      window.removeEventListener('beforeunload', touch);
      touch();
    };
  }, [pageKey, filters, isDefault]);

  var setFilters = useCallback(function(partial) {
    setFiltersState(function(prev) {
      var next = typeof partial === 'function' ? partial(prev) : partial;
      return Object.assign({}, prev, next);
    });
  }, []);

  var resetFilters = useCallback(function() {
    FilterStorage.remove(pageKey);
    setFiltersState(defaultsRef.current);
    setWasRestored(false);
  }, [pageKey]);

  var dismissRestored = useCallback(function() {
    setWasRestored(false);
  }, []);

  return {
    filters: filters,
    setFilters: setFilters,
    resetFilters: resetFilters,
    defaults: defaultsRef.current,
    isDefault: isDefault,
    wasRestored: wasRestored && !isDefault,
    dismissRestored: dismissRestored,
  };
};

export default usePersistedFilters;
